import { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, deleteDoc, addDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { Plus, Trash2, Loader2, Image as ImageIcon, MessageSquareQuote } from 'lucide-react';

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    review: '',
    image: ''
  });

  useEffect(() => {
    const q = query(collection(db, 'testimonials'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      setTestimonials(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (error) => {
      console.error("Error fetching testimonials:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.review) return alert("الاسم والرأي مطلوبان");

    setIsAdding(true);
    try {
      await addDoc(collection(db, 'testimonials'), {
        name: formData.name,
        review: formData.review,
        image: formData.image,
        createdAt: serverTimestamp()
      });
      setFormData({ name: '', review: '', image: '' });
      fetchTestimonials();
    } catch (error) {
      console.error("Error adding testimonial", error);
      alert("حدث خطأ أثناء الإضافة.");
    } finally {
      setIsAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا التقييم؟")) return;
    setIsDeleting(id);
    try {
      await deleteDoc(doc(db, 'testimonials', id));
      setTestimonials(prev => prev.filter(t => t.id !== id));
    } catch (error) {
      console.error("Error deleting", error);
      alert("حدث خطأ أثناء الحذف.");
    } finally {
      setIsDeleting(null);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-2">آراء الطلاب</h1>
        <p className="text-slate-500 dark:text-zinc-400">إدارة التقييمات وآراء الطلاب التي تظهر في الصفحة الرئيسية.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 items-start">
        {/* Add Form */}
        <div className="lg:col-span-1 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl p-6 shadow-sm">
          <h2 className="text-xl font-bold mb-6 text-slate-900 dark:text-white flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary-500" /> إضافة رأي جديد
          </h2>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-1">اسم الطالب <span className="text-red-500">*</span></label>
              <input
                type="text" required
                value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2.5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 transition-all font-bold text-slate-900 dark:text-white"
                placeholder="مثال: أحمد محمد"
              />
            </div>
            <div>
              <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-1">صورة الطالب (رابط URL) <span className="text-slate-400 font-normal text-md">(اختياري)</span></label>
              <div className="relative">
                <ImageIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="url"
                  value={formData.image} onChange={e => setFormData({ ...formData, image: e.target.value })}
                  className="w-full pr-10 pl-4 py-2.5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 transition-all text-left font-bold text-slate-900 dark:text-white"
                  placeholder="https://example.com/image.jpg"
                  dir="ltr"
                />
              </div>
            </div>
            <div>
              <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-1">رأي الطالب <span className="text-red-500">*</span></label>
              <textarea
                required rows="4"
                value={formData.review} onChange={e => setFormData({ ...formData, review: e.target.value })}
                className="w-full px-4 py-2.5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 resize-none transition-all font-bold text-slate-900 dark:text-white"
                placeholder="دورة رائعة جداً واستفدت منها الكثير..."
              ></textarea>
            </div>
            <button
              type="submit" disabled={isAdding}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 mt-4 disabled:opacity-50"
            >
              {isAdding ? <Loader2 className="w-5 h-5 animate-spin" /> : 'نشر التقييم'}
            </button>
          </form>
        </div>

        {/* List of Testimonials */}
        <div className="lg:col-span-2 space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-500">
              <Loader2 className="w-10 h-10 animate-spin text-primary-600 mb-4" />
              <p className="font-bold">جاري تحميل البيانات...</p>
            </div>
          ) : testimonials.length === 0 ? (
            <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl p-10 flex flex-col items-center justify-center text-center shadow-sm">
              <MessageSquareQuote className="w-16 h-16 text-slate-200 dark:text-white/5 mb-4" />
              <h3 className="text-xl font-bold text-slate-700 dark:text-zinc-300 mb-2">لا توجد آراء للطلاب بعد</h3>
              <p className="text-slate-500 dark:text-zinc-500 max-w-sm">يمكنك إضافة التقييمات الأولى باستخدام النموذج الجانبي لتظهر فوراً في الصفحة الرئيسية.</p>
            </div>
          ) : (
            testimonials.map(item => (
              <div key={item.id} className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-2xl p-6 shadow-sm flex flex-col sm:flex-row gap-5 items-start sm:items-center relative group overflow-hidden">
                {item.image ? (
                  <img src={item.image} alt={item.name} className="w-16 h-16 rounded-full object-cover bg-slate-100 shrink-0 border-2 border-white dark:border-zinc-800 shadow-sm" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 flex items-center justify-center text-xl font-bold shrink-0 border-2 border-white dark:border-zinc-800 shadow-sm">
                    {item.name.charAt(0)}
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">{item.name}</h3>
                  <p className="text-slate-600 dark:text-zinc-400 leading-relaxed font-medium">"{item.review}"</p>
                </div>
                <button
                  onClick={() => handleDelete(item.id)}
                  disabled={isDeleting === item.id}
                  className="sm:opacity-0 sm:group-hover:opacity-100 bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 p-3 rounded-xl transition-all absolute left-6 top-6 sm:static"
                  title="حذف التقييم"
                >
                  {isDeleting === item.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
